import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  resolveDashboardPeriodRange,
  type DashboardPeriodState,
} from "@/features/dashboard/period";
import type { DashboardPeriodMetadata } from "@/features/dashboard/types";

const labels = [
  "Hoje",
  "Esta semana",
  "Este mês",
  "Últimos 30 dias",
  "Último ano",
  "Todo o período",
  "Personalizar",
] as const;
type Label = (typeof labels)[number];
const date = (day: string) => new Date(`${day}T12:00:00Z`);
const shift = (day: string, days: number) =>
  new Date(date(day).getTime() + days * 86400000).toISOString().slice(0, 10);
const numeric = (day: string) =>
  `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;
const rangeLabel = (from: string, to: string) =>
  `${numeric(from)}–${numeric(to)}`;
const bucketLabel = (from: string, to: string) =>
  `${from.slice(8, 10)}/${from.slice(5, 7)}–${numeric(to)}`;
const monthName = (day: string) =>
  date(day).toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
const dayName = (day: string) =>
  date(day).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
const monthShift = (day: string, n: number) =>
  new Date(
    Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1 + n, 1, 12),
  )
    .toISOString()
    .slice(0, 10);

function chosen(period: DashboardPeriodState): Label {
  if (period.period === "custom") {
    if (period.from && period.to) {
      const days =
        (date(period.to).getTime() - date(period.from).getTime()) / 86400000;
      if (
        days === 29 &&
        period.to ===
          new Intl.DateTimeFormat("sv-SE", {
            timeZone: "America/Sao_Paulo",
          }).format(new Date())
      )
        return "Últimos 30 dias";
      if (
        days === 364 &&
        period.to ===
          new Intl.DateTimeFormat("sv-SE", {
            timeZone: "America/Sao_Paulo",
          }).format(new Date())
      )
        return "Último ano";
    }
    return "Personalizar";
  }
  return (
    {
      today: "Hoje",
      week: "Esta semana",
      month: "Este mês",
      all: "Todo o período",
    } as const
  )[period.period];
}

export function SuperPeriod({
  period,
  pendingPeriod,
  metadata,
  updating,
  onChange,
}: {
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  metadata: DashboardPeriodMetadata;
  updating?: boolean;
  onChange: (next: DashboardPeriodState) => void;
}) {
  const [menu, setMenu] = useState(false);
  const [calendar, setCalendar] = useState(false);
  const [month, setMonth] = useState(metadata.to);
  const [draft, setDraft] = useState({ start: "", end: "" });
  const [saved, setSaved] = useState<{ start: string; end: string } | null>(
    null,
  );
  const trigger = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [focusIndex, setFocusIndex] = useState(2);
  const selected = chosen(period);
  const today = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "America/Sao_Paulo",
  }).format(new Date());
  const selectedRange =
    period.period === "all"
      ? { from: metadata.from ?? metadata.to, to: metadata.to }
      : period.period === "custom"
        ? { from: period.from!, to: period.to! }
        : resolveDashboardPeriodRange(period)!;
  const close = () => {
    setMenu(false);
    setCalendar(false);
    opener.current?.focus();
  };
  useEffect(() => {
    if (!menu && !calendar) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [menu, calendar]);
  useEffect(() => {
    if (menu)
      menuRef.current
        ?.querySelectorAll<HTMLButtonElement>("button")
        [focusIndex]?.focus();
  }, [menu, focusIndex]);
  useEffect(() => {
    if (calendar)
      calendarRef.current
        ?.querySelector<HTMLButtonElement>(
          ".lnk209-calendar-day:not(:disabled)",
        )
        ?.focus();
  }, [calendar]);
  const select = (option: Label) => {
    setMenu(false);
    if (option === "Personalizar") {
      setMonth(saved?.end ?? selectedRange.to);
      setDraft(
        saved ??
          (period.period === "custom" && selected === "Personalizar"
            ? { start: period.from!, end: period.to! }
            : { start: "", end: "" }),
      );
      setCalendar(true);
      return;
    }
    setCalendar(false);
    const from =
      option === "Últimos 30 dias" ? shift(today, -29) : shift(today, -364);
    onChange({
      ...period,
      ...(option === "Últimos 30 dias" || option === "Último ano"
        ? { period: "custom" as const, from, to: today }
        : {
            period: (
              {
                Hoje: "today",
                "Esta semana": "week",
                "Este mês": "month",
                "Todo o período": "all",
              } as const
            )[option],
            from: undefined,
            to: undefined,
          }),
    });
    opener.current?.focus();
  };
  const first = `${month.slice(0, 7)}-01`;
  const monday = shift(first, -((date(first).getUTCDay() + 6) % 7));
  return (
    <div
      className="lnk209-period-bar"
      ref={root}
      aria-busy={updating || undefined}
    >
      <div className="lnk209-tab-anchor">
        <div
          className="lnk209-presets"
          role="group"
          aria-label={`Período selecionado: ${rangeLabel(selectedRange.from, selectedRange.to)}`}
        >
          {labels.map((option) => (
            <button
              key={option}
              aria-pressed={selected === option}
              aria-expanded={option === "Personalizar" ? calendar : undefined}
              title={
                option === "Personalizar" && saved
                  ? rangeLabel(saved.start, saved.end)
                  : undefined
              }
              onClick={(event) => {
                opener.current = event.currentTarget;
                select(option);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="lnk209-mobile-period">
        <button
          ref={trigger}
          className="lnk209-mobile-period-trigger"
          aria-haspopup="menu"
          aria-expanded={menu}
          aria-label={
            selected === "Personalizar"
              ? `Período personalizado: ${rangeLabel(selectedRange.from, selectedRange.to)}`
              : undefined
          }
          title={
            selected === "Personalizar"
              ? rangeLabel(selectedRange.from, selectedRange.to)
              : undefined
          }
          onClick={(event) => {
            opener.current = event.currentTarget;
            setFocusIndex(labels.indexOf(selected));
            setMenu(!menu);
          }}
          onKeyDown={(event) => {
            if (
              !menu &&
              (event.key === "ArrowDown" || event.key === "ArrowUp")
            ) {
              event.preventDefault();
              opener.current = event.currentTarget;
              setFocusIndex(labels.indexOf(selected));
              setMenu(true);
            }
          }}
        >
          {selected === "Personalizar"
            ? bucketLabel(selectedRange.from, selectedRange.to)
            : selected}
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        {menu && (
          <div
            className="lnk209-mobile-period-menu"
            role="menu"
            aria-label="Escolher período"
            ref={menuRef}
          >
            {labels.map((option, index) => (
              <button
                key={option}
                role="menuitemradio"
                aria-checked={selected === option}
                onClick={() => select(option)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    setFocusIndex(
                      (index +
                        (event.key === "ArrowDown" ? 1 : labels.length - 1)) %
                        labels.length,
                    );
                  }
                }}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
      {calendar && (
        <div className="lnk209-calendar-position">
          <div
            className="lnk209-calendar"
            role="dialog"
            aria-label="Escolher datas"
            ref={calendarRef}
          >
            <div className="lnk209-calendar-head">
              <button
                aria-label="Mês anterior"
                onClick={() => setMonth(monthShift(month, -1))}
              >
                <ChevronLeft size={17} />
              </button>
              <strong>{monthName(month)}</strong>
              <button
                aria-label="Próximo mês"
                disabled={month.slice(0, 7) >= today.slice(0, 7)}
                onClick={() => setMonth(monthShift(month, 1))}
              >
                <ChevronRight size={17} />
              </button>
            </div>
            <div className="lnk209-calendar-grid">
              {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((day) => (
                <span key={day} aria-hidden="true">
                  {day}
                </span>
              ))}
              {Array.from({ length: 42 }, (_, i) => shift(monday, i)).map(
                (day) =>
                  day.slice(0, 7) === month.slice(0, 7) ? (
                    <button
                      key={day}
                      className="lnk209-calendar-day"
                      aria-label={dayName(day)}
                      aria-pressed={day === draft.start || day === draft.end}
                      data-in-range={Boolean(
                        draft.end && day > draft.start && day < draft.end,
                      )}
                      disabled={day > today}
                      onClick={() =>
                        setDraft(
                          !draft.start || draft.end || day < draft.start
                            ? { start: day, end: "" }
                            : { ...draft, end: day },
                        )
                      }
                    >
                      {Number(day.slice(-2))}
                    </button>
                  ) : (
                    <span key={day} aria-hidden="true" />
                  ),
              )}
            </div>
            <div className="lnk209-calendar-selection" aria-live="polite">
              {draft.start
                ? `${numeric(draft.start)}${draft.end ? `–${numeric(draft.end)}` : " · escolha o fim"}`
                : "Selecione início e fim"}
            </div>
            <div className="lnk209-calendar-actions">
              <button onClick={close}>Cancelar</button>
              <button
                disabled={!draft.start || !draft.end || Boolean(pendingPeriod)}
                onClick={() => {
                  setSaved(draft);
                  onChange({
                    period: "custom",
                    from: draft.start,
                    to: draft.end,
                    history: period.history,
                  });
                  close();
                }}
              >
                Aplicar intervalo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
